import { useState, FormEvent } from 'react'
import { PasswordInput, Button, Box, Text, Alert, Stack } from '@mantine/core'
import { IconAlertCircle, IconLockOpen } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { platformChangePassword } from '../../api/platformClient'
import { usePlatformAuth } from '../../hooks/usePlatformAuth'

export default function PlatformChangePasswordPage() {
  const { clearMustChangePassword } = usePlatformAuth()
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setLoading(true)
    try {
      await platformChangePassword(currentPassword, newPassword)
      clearMustChangePassword()
      navigate('/platform/dashboard', { replace: true })
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Could not change password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box
      style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(circle at 30% 20%, #1a2138 0%, #0a0e17 55%)',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <Box style={{ width: 400 }}>
        <Stack align="center" gap={4} mb={28}>
          <Box style={{
            width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #f5a623, #d98c1a)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12,
          }}>
            <IconLockOpen size={26} color="#0a0e17" />
          </Box>
          <Text style={{ color: '#fff', fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }}>
            Set a new password
          </Text>
          <Text style={{ color: '#6b7690', fontSize: 13, textAlign: 'center', maxWidth: 300 }}>
            You're signing in with a temporary password. Choose a new one before continuing.
          </Text>
        </Stack>

        <Box
          component="form"
          onSubmit={handleSubmit}
          style={{ background: '#131a2c', border: '1px solid #232c44', borderRadius: 14, padding: '28px 26px' }}
        >
          <Stack gap={16}>
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" radius="md">
                {error}
              </Alert>
            )}
            <PasswordInput
              label="Current (temporary) password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoFocus
              styles={{
                label: { color: '#9aa3ba', fontSize: 12, fontWeight: 600, marginBottom: 4 },
                input: { background: '#0e1420', border: '1px solid #232c44', color: '#fff' },
              }}
            />
            <PasswordInput
              label="New password"
              description="At least 8 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              styles={{
                label: { color: '#9aa3ba', fontSize: 12, fontWeight: 600, marginBottom: 4 },
                description: { color: '#4b5468', fontSize: 11 },
                input: { background: '#0e1420', border: '1px solid #232c44', color: '#fff' },
              }}
            />
            <PasswordInput
              label="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              styles={{
                label: { color: '#9aa3ba', fontSize: 12, fontWeight: 600, marginBottom: 4 },
                input: { background: '#0e1420', border: '1px solid #232c44', color: '#fff' },
              }}
            />
            <Button
              type="submit"
              loading={loading}
              fullWidth
              size="md"
              mt={4}
              styles={{
                root: {
                  background: 'linear-gradient(135deg, #f5a623, #d98c1a)',
                  color: '#0a0e17', fontWeight: 700,
                },
              }}
            >
              Update password and continue
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}
